import {
  ArrowLeft,
  RefreshCw,
  Save,
  WifiOff,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import DiarioFormSections from "@/features/diario/components/DiarioFormSections";
import DiarioModals from "@/features/diario/components/DiarioModals";
import { registrarAlteracao } from "@/features/diario/services/diarioAlteracoesService";
import { compressImage as svcCompressImage } from "@/features/diario/services/relatoriosService";
import { FotoTagsPopover } from "@/features/midia/components/FotoTagsPopover";
import PhotoViewer from "@/features/midia/components/PhotoViewer";
import { useFotosComTags } from "@/features/midia/hooks/useFotoTags";
import { useAuth } from "@/features/sistema/hooks/useAuth";
import { useDarkMode } from "@/features/sistema/hooks/useDarkMode";
import { useDraft } from "@/features/sistema/hooks/useDraft";
import { useOnlineStatus } from "@/features/sistema/hooks/useOnlineStatus";
import { useRouteRefresh } from "@/features/sistema/hooks/useRouteRefresh";
import { supabase } from "@/features/sistema/services/supabaseClient";

// ---------------------------------------------------------------------------
// ACOR: Constante de TTL do rascunho local (7 dias)
const DRAFT_TTL_HOURS = 168; // 7 * 24

const FORM_INICIAL: FormData = {
  numero: 1,
  data: "",
  status: "rascunho",
  clima_manha: null,
  clima_tarde: null,
  clima_noite: null,
  mao_obra: [],
  equipamentos: [],
  atividades: [],
  ocorrencias: [],
  comentarios: "",
  anexos: [],
  alteracoes_projeto: { texto: "", anexos: [] }
};
// ---------------------------------------------------------------------------

interface MaoItem {
  nome: string;
  quantidade: number;
}

interface EquipItem {
  nome: string;
  quantidade: number;
}

interface AtividadeItem {
  descricao: string;
  porcentagem: number;
  status: "Em andamento" | "Concluída" | "Pausada";
}

interface AnexoItem {
  id: string;
  url: string;
  nome: string;
  tipo: "foto" | "video" | "anexo";
  criado_em?: string;
}

interface Relatorio {
  id: string;
  numero: number;
  data: string;
  status: "rascunho" | "submetido" | "aprovado";
  clima_manha: string | null;
  clima_tarde: string | null;
  clima_noite: string | null;
  mao_obra: MaoItem[] | null;
  equipamentos: EquipItem[] | null;
  atividades: string | null;
  ocorrencias: string | null;
  comentarios: string | null;
  alteracoes_projeto: { texto: string } | null; 
  ultima_modificacao: string;
}

interface FormData {
  numero: number;
  data: string;
  status: "rascunho" | "submetido" | "aprovado";
  clima_manha: string | null;
  clima_tarde: string | null;
  clima_noite: string | null;
  mao_obra: MaoItem[];
  equipamentos: EquipItem[];
  atividades: AtividadeItem[];
  ocorrencias: string[];
  comentarios: string;
  anexos: AnexoItem[];
  alteracoes_projeto: { 
    texto: string;
    anexos?: Array<{ url: string; nome: string; tipo: 'imagem' | 'video' | 'arquivo' }>;
  } | null;
}

interface DiarioEditarProps {
  // Removido modoEscuro prop
}

export default function DiarioEditar({}: DiarioEditarProps) {
  const { id: obraId, relatorioId } = useParams<{ id: string; relatorioId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isOnline, forceCheckConnectivity, networkStatus } = useOnlineStatus();
  const [isDarkMode] = useDarkMode();
  const [relatorio, setRelatorio] = useState<Relatorio | null>(null);
  // -------------------------------------------------------------------------
  // ACOR: Estado para exibir badge de "Rascunho salvo"
  // -------------------------------------------------------------------------
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);

  // Controle de histórico para o modal da galeria
  const modalHistoryRef = useRef(false);
  const ignoreNextPopRef = useRef(false);
  // removido estado não utilizado: dadosCarregados
  const [isFromCache, setIsFromCache] = useState(false);

  // Estados para o popover de tags
  const [tagsPopoverOpen, setTagsPopoverOpen] = useState(false);
  const [fotoParaTags, setFotoParaTags] = useState<AnexoItem | null>(null);
  const [tagsPopoverPosition, setTagsPopoverPosition] = useState<{ x: number; y: number } | undefined>();

  // Estados para modo de seleção múltipla
  const [modoSelecao, setModoSelecao] = useState(false);
  const [fotosSelecionadas, setFotosSelecionadas] = useState<Set<string>>(new Set());

  const draftKey: string | null = relatorioId ? `diario-draft-${relatorioId}` : null;
  const {
    value: form, setValue: setForm,
    lastSavedAt, pendingSync, setPendingSync,
    loadDraft, scheduleSave, clearDraft
  } = useDraft<FormData>({ key: draftKey, initial: FORM_INICIAL, ttlHours: DRAFT_TTL_HOURS });

  // Hook para carregar tags das fotos do diário (para exibir indicador)
  const fotoIdsDiario = useMemo(() =>
    form.anexos.filter(a => a.tipo === 'foto').map(f => f.id),
    [form.anexos]
  );
  const { tagsMap: fotoTagsMap, recarregar: recarregarTagsFotos } = useFotosComTags(fotoIdsDiario);

  const setFormFromDatabase = useCallback(
    (data: any, parsedAtividades: AtividadeItem[], parsedOcorrencias: string[]) => {
      let dataFormatada = data.data;
      if (data.data) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(data.data)) {
          dataFormatada = data.data;
        } else {
          const dateObj = new Date(data.data);
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          dataFormatada = `${year}-${month}-${day}`;
        }
      }

      const alteracoesProjetoData = data.alteracoes_projeto || null;
      const alteracoesProjeto = alteracoesProjetoData
        ? {
            texto: alteracoesProjetoData.texto || "",
            anexos: alteracoesProjetoData.anexos || []
          }
        : { texto: "", anexos: [] };

      setForm({
        numero: data.numero,
        data: dataFormatada,
        status: data.status,
        clima_manha: data.clima_manha,
        clima_tarde: data.clima_tarde,
        clima_noite: data.clima_noite,
        mao_obra: data.mao_obra || [],
        equipamentos: data.equipamentos || [],
        atividades: parsedAtividades,
        ocorrencias: parsedOcorrencias,
        comentarios: data.comentarios || "",
        anexos: [],
        alteracoes_projeto: alteracoesProjeto
      });
    },
    [setForm]
  );

  const carregarAnexos = useCallback(async () => {
    if (!relatorioId || !isOnline) {
      console.log('?? Offline: pulando carregamento de anexos');
      return;
    }

    try {
      let anexosData: AnexoItem[] = [];

      const { data: fotos, error: fotosError } = await supabase
        .from("fotos")
        .select("id, url, nome, criado_em")
        .eq("relatorio_id", relatorioId);

      if (!fotosError && fotos) {
        anexosData = anexosData.concat(
          fotos.map((f) => ({ ...f, tipo: "foto" as const }))
        );
      }

      const { data: videos, error: videosError } = await supabase
        .from("videos")
        .select("id, url, nome, criado_em")
        .eq("relatorio_id", relatorioId);

      if (!videosError && videos) {
        anexosData = anexosData.concat(
          videos.map((v) => ({ ...v, tipo: "video" as const }))
        );
      }

      const { data: anexos, error: anexosError } = await supabase
        .from("anexos")
        .select("id, url, nome, criado_em")
        .eq("relatorio_id", relatorioId);

      if (!anexosError && anexos) {
        anexosData = anexosData.concat(
          anexos.map((a) => ({ ...a, tipo: "anexo" as const }))
        );
      }

      setForm((prev) => ({ ...prev, anexos: anexosData }));
      console.log(`? ${anexosData.length} anexos carregados`);
    } catch (e) {
      console.warn("Erro ao carregar anexos:", e);
    }
  }, [isOnline, relatorioId, setForm]);

  // Estados dos modais
  const [showMaoModal, setShowMaoModal] = useState(false);
  const [showEquipModal, setShowEquipModal] = useState(false);
  const [showAtvModal, setShowAtvModal] = useState(false);
  const [showOcorrModal, setShowOcorrModal] = useState(false);

  // Navegação por teclado para o modal de galeria
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!galleryOpen) return;

    switch (event.key) {
      case 'Escape':
        closeGallery();
        break;
      case 'ArrowLeft':
        if (selectedMediaIndex !== null && selectedMediaIndex > 0) {
          setSelectedMediaIndex(selectedMediaIndex - 1);
        }
        break;
      case 'ArrowRight':
        const mediaItems = form.anexos.filter(a => a.tipo === "foto" || a.tipo === "video");
        if (selectedMediaIndex !== null && selectedMediaIndex < mediaItems.length - 1) {
          setSelectedMediaIndex(selectedMediaIndex + 1);
        }
        break;
    }
  }, [galleryOpen, selectedMediaIndex, form.anexos]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    // ❌ nada de carregar aqui; o enter de rota é pelo useRouteRefresh
  }, [relatorioId, user, authLoading, navigate]);



  // ✅ Salvamento automático é gerenciado pelo hook useDraft

  // ✅ Flush do rascunho é gerenciado pelo hook useDraft

  // Intercepta o botão voltar do navegador/smartphone quando a galeria estiver aberta
  useEffect(() => {
    const onPopState = (_e: PopStateEvent) => {
      // Se foi um back programático disparado pelo closeGallery, ignorar este ciclo
      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        return;
      }
      // Se a galeria está aberta, usa o voltar apenas para fechar o modal
      if (galleryOpen) {
        closeGallery(true /* fromPopState */);
        return; // não navega de fato
      }
      // Caso contrário, comportamento normal do router
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [galleryOpen]);

  // useEffect para gerenciar event listeners do modal de galeria
  useEffect(() => {
    if (galleryOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [galleryOpen, handleKeyDown]);

  // ✅ FUNÇÃO MELHORADA COM LÓGICA DE CACHE MAIS INTELIGENTE
  const carregarRelatorio = useCallback(async () => {
    if (!relatorioId) return;
    
    try {
      setLoading(true);
      setErro(null);
      setIsFromCache(false);
      
      // ✅ Verificar dados locais usando o hook useDraft
      const local = await loadDraft();
      let dadosLocais = local;
      let hasPendingSync = local?.pendingSync || false;
      let localSavedAt: Date | null = local?.savedAt ? new Date(local.savedAt) : null;

      if (isOnline) {
        // ✅ Online: SEMPRE tentar buscar do servidor primeiro
        try {
          const { data, error } = await supabase
            .from("relatorios")
            .select("*")
            .eq("id", relatorioId)
            .single();

          if (error) throw error;
          
          setRelatorio(data);
          const serverSavedAt: Date | null =
            data?.ultima_modificacao ? new Date(data.ultima_modificacao) : null;
          
          // Parse das atividades e ocorrências
          let parsedAtividades: AtividadeItem[] = [];
          let parsedOcorrencias: string[] = [];
          
          try {
            if (data.atividades) {
              parsedAtividades = JSON.parse(data.atividades);
            }
            if (data.ocorrencias) {
              parsedOcorrencias = JSON.parse(data.ocorrencias);
            }
          } catch (e) {
            console.warn("Erro ao parsear atividades ou ocorrências:", e);
          }

          // ✅ Preferir LOCAL se for MAIS NOVO que o servidor
          const localIsNewer =
            !!(dadosLocais && localSavedAt && (!serverSavedAt || localSavedAt > serverSavedAt));

          if (localIsNewer) {
            setForm(dadosLocais);
            setIsFromCache(true);
          } else if (hasPendingSync && dadosLocais) {
            // Mesclar dados do servidor com alterações locais pendentes
            const mergedData = {
              ...dadosLocais,
              // Manter dados do servidor para campos que não foram editados localmente
              numero: data.numero,
              data: data.data,
              status: data.status,
              clima_manha: data.clima_manha,
              clima_tarde: data.clima_tarde,
              clima_noite: data.clima_noite,
              // Manter alterações locais se existirem
              mao_obra: dadosLocais.mao_obra || data.mao_obra || [],
              equipamentos: dadosLocais.equipamentos || data.equipamentos || [],
              atividades: dadosLocais.atividades || parsedAtividades,
              ocorrencias: dadosLocais.ocorrencias || parsedOcorrencias,
              comentarios: dadosLocais.comentarios || data.comentarios || "",
              anexos: []
            };
            
            setForm(mergedData);
            setIsFromCache(true);
            setErro('Dados mesclados com alterações locais pendentes. Salve para sincronizar.');
          } else {
            setFormFromDatabase(data, parsedAtividades, parsedOcorrencias);
            setIsFromCache(false);
            
            // Limpar rascunho local apenas se servidor >= local
            if (dadosLocais && !hasPendingSync) {
              if (!localSavedAt || (serverSavedAt && serverSavedAt >= localSavedAt)) {
                await clearDraft();
              }
            }
          }

          // Carregar anexos online
          await carregarAnexos();
          
        } catch (err) {
          console.error('Erro ao carregar do servidor:', err);
          
          // ✅ Fallback: usar dados locais apenas se disponíveis
          if (dadosLocais) {
            setForm(dadosLocais);
            setIsFromCache(true);
            
            // Tentar carregar dados básicos do relatório do cache geral
            const cachedRelatorios = localStorage.getItem(`relatorios_obra_${obraId}`);
            if (cachedRelatorios) {
              try {
                const relatorios = JSON.parse(cachedRelatorios);
                const relatorioBasico = relatorios.find((r: any) => r.id === relatorioId);
                if (relatorioBasico) {
                  setRelatorio({
                    id: relatorioBasico.id,
                    numero: relatorioBasico.numero,
                    data: relatorioBasico.data,
                    status: relatorioBasico.status,
                    clima_manha: relatorioBasico.clima,
                    clima_tarde: null,
                    clima_noite: null,
                    mao_obra: dadosLocais.mao_obra,
                    equipamentos: dadosLocais.equipamentos,
                    atividades: JSON.stringify(dadosLocais.atividades),
                    ocorrencias: JSON.stringify(dadosLocais.ocorrencias),
                    comentarios: dadosLocais.comentarios,
                    alteracoes_projeto: dadosLocais.alteracoes_projeto ?? { texto: "" },
                    ultima_modificacao: relatorioBasico.updated_at || new Date().toISOString()
                  });
                }
              } catch (e) {
                console.warn('Erro ao carregar relatório básico do cache:', e);
              }
            }
          } else {
            throw err; // Re-throw se não há cache disponível
          }
        }

      } else {
        // ✅ Offline: Buscar do cache local
        console.log('📱 Modo offline, buscando do cache...');
        
        if (dadosLocais) {
          console.log('📱 Dados do diário carregados do cache local');
          setForm(dadosLocais);
          setIsFromCache(true);
          
          // Tentar carregar dados básicos do relatório do cache
          const cachedRelatorios = localStorage.getItem(`relatorios_obra_${obraId}`);
          if (cachedRelatorios) {
            try {
              const relatorios = JSON.parse(cachedRelatorios);
              const relatorioBasico = relatorios.find((r: any) => r.id === relatorioId);
              if (relatorioBasico) {
                setRelatorio({
                  id: relatorioBasico.id,
                  numero: relatorioBasico.numero,
                  data: relatorioBasico.data,
                  status: relatorioBasico.status,
                  clima_manha: relatorioBasico.clima,
                  clima_tarde: null,
                  clima_noite: null,
                  mao_obra: dadosLocais.mao_obra,
                  equipamentos: dadosLocais.equipamentos,
                  atividades: JSON.stringify(dadosLocais.atividades),
                  ocorrencias: JSON.stringify(dadosLocais.ocorrencias),
                  comentarios: dadosLocais.comentarios,
                  alteracoes_projeto: dadosLocais.alteracoes_projeto ?? { texto: "" },
                  ultima_modificacao: relatorioBasico.updated_at || new Date().toISOString()
                });
              }
            } catch (e) {
              console.warn('Erro ao carregar relatório básico do cache:', e);
            }
          }
        } else {
          // Sem dados em cache
          setErro('Nenhum diário encontrado no cache. Conecte-se à internet para carregar os dados.');
          console.log('📱 Nenhum diário encontrado no cache');
        }
      }


    } catch (err) {
      console.error('❌ Erro ao carregar diário:', err);
      setErro((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [carregarAnexos, clearDraft, isOnline, loadDraft, obraId, relatorioId, setForm, setFormFromDatabase, setIsFromCache, setErro, setRelatorio]);

  // ✅ Atualizar SOMENTE quando a rota mudar (ou quando dispararmos app:refresh)
  useRouteRefresh(carregarRelatorio);


  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!relatorioId) return;
    
    try {
      setSaving(true);
      setErro(null);

      if (isOnline) {
        // ✅ Online: Salvar no servidor
        console.log('💾 Salvando no servidor...');
        
        const { error } = await supabase
          .from("relatorios")
          .update({
            numero: form.numero,
            data: form.data,
            status: form.status,
            clima_manha: form.clima_manha,
            clima_tarde: form.clima_tarde,
            clima_noite: form.clima_noite,
            mao_obra: form.mao_obra,
            equipamentos: form.equipamentos,
            atividades: form.atividades.length > 0 ? JSON.stringify(form.atividades) : null,
            ocorrencias: form.ocorrencias.length > 0 ? JSON.stringify(form.ocorrencias) : null,
            comentarios: form.comentarios || null,
            // jsonb: envia objeto quando houver texto ou anexos; senão, null
            alteracoes_projeto: ((form.alteracoes_projeto?.texto || "").trim().length > 0 || (form.alteracoes_projeto?.anexos?.length ?? 0) > 0)
              ? { 
                  texto: (form.alteracoes_projeto?.texto || "").trim(),
                  anexos: form.alteracoes_projeto?.anexos || []
                } 
              : null,
            ultima_modificacao: new Date().toISOString(),
          })
          .eq("id", relatorioId);

        if (error) throw error;
        
        // ✅ Registrar alterações de projeto como thread separada (se houver conteúdo)
        if (form.alteracoes_projeto && obraId) {
          const temTexto = (form.alteracoes_projeto.texto || "").trim().length > 0;
          const temAnexos = (form.alteracoes_projeto.anexos?.length ?? 0) > 0;
          
          if (temTexto || temAnexos) {
            try {
              await registrarAlteracao({
                obraId,
                relatorioId,
                texto: form.alteracoes_projeto.texto,
                anexos: form.alteracoes_projeto.anexos || []
              });
            } catch (e) {
              console.warn('Erro ao registrar alterações nas notificações:', e);
              // Não bloqueia o salvamento principal
            }
          }
        }
        
        // ✅ Limpar dados salvos localmente após sucesso
        await clearDraft();
        
        // 🔹 avisar telas relacionadas para recarregar (manual & previsível)
        window.dispatchEvent(new CustomEvent('app:refresh', { detail: { scope: `/obras/${obraId}/diarios` } }));
        window.dispatchEvent(new CustomEvent('app:refresh', { detail: { scope: `/obras/${obraId}/diarios/${relatorioId}` } }));
        // Redirecionar para lista de diários
        navigate(`/obras/${obraId}/diarios`);
      } else {
        // ✅ Offline: Salvar apenas localmente
        if (!draftKey) {
          setErro('Erro: ID do relatório não disponível para salvamento offline.');
          return;
        }
        
        const saveData = {
          ...form,
          savedAt: new Date().toISOString(),
          pendingSync: true
        };
        localStorage.setItem(draftKey, JSON.stringify(saveData));
        setPendingSync(true);
        
        // Mostrar mensagem de sucesso mas não redirecionar
        setErro('Diário salvo localmente. Será sincronizado quando você estiver online.');
      }
      
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setErro((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function updateField(field: keyof FormData, value: any) {
    const next = { ...form, [field]: value } as FormData;
    setForm(next);
    scheduleSave(next);
  }

  // Funções para Mão de Obra
  function updateQtdMao(idx: number, nova: number) {
    const novaLista = [...form.mao_obra];
    novaLista[idx] = { ...novaLista[idx], quantidade: Math.max(0, nova) };
    updateField('mao_obra', novaLista);
  }

  function removeMao(idx: number) {
    const novaLista = [...form.mao_obra];
    novaLista.splice(idx, 1);
    updateField('mao_obra', novaLista);
  }

  // Funções para Equipamentos
  function updateQtdEquip(idx: number, nova: number) {
    const novaLista = [...form.equipamentos];
    novaLista[idx] = { ...novaLista[idx], quantidade: Math.max(0, nova) };
    updateField('equipamentos', novaLista);
  }

  function removeEquip(idx: number) {
    const novaLista = [...form.equipamentos];
    novaLista.splice(idx, 1);
    updateField('equipamentos', novaLista);
  }



  // Funções para Anexos
  async function handleFileUpload(
    e: React.ChangeEvent<HTMLInputElement>,
    tipo: "foto" | "video" | "anexo"
  ) {
    const files = e.target.files;
    if (!files || !relatorioId || !user) return;

    if (!isOnline) {
      setErro('Upload de arquivos não disponível offline. Conecte-se à internet.');
      return;
    }

    setUploading(true);
    const novosAnexos: AnexoItem[] = [];

    for (const file of Array.from(files)) {
      try {
        // Comprimir imagem se necessário
        let processedFile = file;
        if (tipo === "foto" && file.type.startsWith('image/')) {
          console.log(`📷 Comprimindo imagem: ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);
          processedFile = await svcCompressImage(file, { maxSizeKB: 200 });
        }

        const fileExt = processedFile.name.split(".").pop();
        const fileName = `${relatorioId}/${tipo}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from("anexos")
          .upload(fileName, processedFile, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          console.error("Erro no upload:", uploadError);
          continue;
        }

        const { data: urlData } = supabase.storage
          .from("anexos")
          .getPublicUrl(fileName);

        if (!urlData.publicUrl) {
          console.error("Erro ao obter URL pública");
          continue;
        }

        // Salvar no banco de dados
        const tableName = tipo === "foto" ? "fotos" : tipo === "video" ? "videos" : "anexos";
        const insertData = {
          relatorio_id: relatorioId,
          url: urlData.publicUrl,
          nome: processedFile.name,
          criado_em: new Date().toISOString(),
        };

        const { error: dbError } = await supabase.from(tableName).insert(insertData);

        if (dbError) {
          console.error("Erro ao salvar no banco:", dbError);
          continue;
        }

        novosAnexos.push({
          id: `${relatorioId}-${processedFile.name}-${Date.now()}`,
          url: urlData.publicUrl,
          nome: processedFile.name,
          tipo,
          criado_em: new Date().toISOString(),
        });
      } catch (e) {
        console.error("Erro ao processar upload:", e);
      }
    }

    setForm(prev => ({
      ...prev,
      anexos: [...prev.anexos, ...novosAnexos],
    }));
    setUploading(false);
  }

  async function removeAnexo(anexo: AnexoItem) {
    if (!isOnline) {
      setErro('Remoção de anexos não disponível offline. Conecte-se à internet.');
      return;
    }

    try {
      // Remover do storage
      const filePath = anexo.url.split("/anexos/")[1];
      await supabase.storage.from("anexos").remove([filePath]);

      // Remover do banco
      const tableName = anexo.tipo === "foto" ? "fotos" : anexo.tipo === "video" ? "videos" : "anexos";
      await supabase.from(tableName).delete().eq("url", anexo.url);

      // Remover do estado
      setForm(prev => ({
        ...prev,
        anexos: prev.anexos.filter((a) => a.url !== anexo.url),
      }));
    } catch (e) {
      console.error("Erro ao remover anexo:", e);
      setErro("Erro ao remover anexo. Tente novamente.");
    }
  }

  // Funções para a galeria
  const mediaItems = form.anexos.filter(a => a.tipo === "foto" || a.tipo === "video");
  const openGallery = (index: number) => {
    setSelectedMediaIndex(index);
    setGalleryOpen(true);
    // Empurra um estado virtual no histórico para capturar o botão voltar
    if (!modalHistoryRef.current) {
      window.history.pushState({ gallery: true }, "");
      modalHistoryRef.current = true;
    }
  };

  // Permite fechar a galeria consumindo o histórico quando o fechamento é via UI
  const closeGallery = (fromPopState: boolean = false) => {
    setSelectedMediaIndex(null);
    setGalleryOpen(false);
    // Se o fechamento não veio do botão voltar, voltamos 1 passo para manter a pilha consistente
    if (!fromPopState && modalHistoryRef.current) {
      // evita que o handler de popstate execute novamente após o back programático
      ignoreNextPopRef.current = true;
      window.history.back();
    }
    modalHistoryRef.current = false;
  };
  const goToPrevious = () => {
    if (selectedMediaIndex !== null && selectedMediaIndex > 0) {
      setSelectedMediaIndex(selectedMediaIndex - 1);
    }
  };
  const goToNext = () => {
    if (selectedMediaIndex !== null && selectedMediaIndex < mediaItems.length - 1) {
      setSelectedMediaIndex(selectedMediaIndex + 1);
    }
  };

  // Handlers para modais
  const handleAddMaoObra = (itens: MaoItem[]) => {
    const existentes = form.mao_obra;
    const novos = itens.filter(item => !existentes.some(e => e.nome === item.nome));
    updateField('mao_obra', [...existentes, ...novos]);
  };

  const handleAddEquipamentos = (itens: EquipItem[]) => {
    const existentes = form.equipamentos;
    const novos = itens.filter(item => !existentes.some(e => e.nome === item.nome));
    updateField('equipamentos', [...existentes, ...novos]);
  };

  const handleAddAtividade = (atividade: AtividadeItem) => {
    const novasAtividades = [...form.atividades, atividade];
    updateField('atividades', novasAtividades);
  };

  const handleAddOcorrencia = (ocorrencia: string) => {
    const novasOcorrencias = [...form.ocorrencias, ocorrencia];
    updateField('ocorrencias', novasOcorrencias);
  };

  // Handler para long press em foto (abre popover de tags)
  const handleFotoLongPress = (anexo: AnexoItem, position: { x: number; y: number }) => {
    setFotoParaTags(anexo);
    setTagsPopoverPosition(position);
    setTagsPopoverOpen(true);
  };

  const fecharTagsPopover = () => {
    setTagsPopoverOpen(false);
    setFotoParaTags(null);
    setTagsPopoverPosition(undefined);
    // Recarregar tags das fotos para atualizar os indicadores
    recarregarTagsFotos();
    // Se estava no modo seleção, sair dele após fechar o popover
    if (modoSelecao) {
      setModoSelecao(false);
      setFotosSelecionadas(new Set());
    }
  };

  // Funções para modo de seleção múltipla
  const toggleModoSelecao = () => {
    setModoSelecao(prev => !prev);
    setFotosSelecionadas(new Set());
  };

  const toggleFotoSelecionada = (fotoId: string) => {
    setFotosSelecionadas(prev => {
      const nova = new Set(prev);
      if (nova.has(fotoId)) {
        nova.delete(fotoId);
      } else {
        nova.add(fotoId);
      }
      return nova;
    });
  };

  const selecionarTodasFotos = () => {
    const fotosDiario = form.anexos.filter(a => a.tipo === 'foto');
    setFotosSelecionadas(new Set(fotosDiario.map(f => f.id)));
  };

  const limparSelecaoFotos = () => {
    setFotosSelecionadas(new Set());
  };

  const abrirTagsMultiplasFotos = () => {
    if (fotosSelecionadas.size === 0) return;
    setTagsPopoverOpen(true);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors ${
        isDarkMode ? "bg-gray-900" : "bg-background"
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-confirmation dark:border-green-400 mx-auto mb-4"></div>
          <p className={`font-ageo-regular transition-colors ${
            isDarkMode ? "text-gray-100" : "text-foreground"
          }`}>
            {isOnline ? 'Carregando diário...' : 'Carregando diário do cache...'}
          </p>
        </div>
      </div>
    );
  }

  if (erro && !relatorio && form.numero === 1) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors ${
        isDarkMode ? "bg-gray-900" : "bg-background"
      }`}>
        <div className="text-center">
          <p className={`font-ageo-regular mb-4 transition-colors ${
            isDarkMode ? "text-red-400" : "text-critical"
          }`}>{erro}</p>
          {isOnline && (
            <button
              onClick={carregarRelatorio}
              className="px-4 py-2 bg-confirmation dark:bg-green-600 text-white rounded-lg hover:bg-confirmation/90 dark:hover:bg-green-700 transition-colors font-ageo-regular flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar novamente
            </button>
          )}
        </div>
      </div>
    );
  }



  return (
    <div className={`${isDarkMode ? "bg-gray-900 text-gray-100" : "bg-background text-foreground"} min-h-[100dvh] overflow-y-auto`}>
      <div className={`sticky top-0 z-10 border-b shadow-sm transition-colors ${
        isDarkMode ? "bg-gray-900 border-gray-700" : "bg-background border-secondary"
      }`}>
        {/* header com mesma régua das demais páginas */}
        <div className="w-full max-w-[1600px] 2xl:max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 bg-transparent">
          {/* ✅ INDICADOR DE STATUS DE CONECTIVIDADE MELHORADO */}
          {(!isOnline || !networkStatus.serverReachable) && (
            <div className={`border rounded-lg p-3 mb-4 transition-colors ${
              !networkStatus.navigatorOnline 
                ? "bg-yellow-100 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800 text-yellow-800 dark:text-yellow-400"
                : "bg-orange-100 dark:bg-orange-900/20 border-orange-300 dark:border-orange-800 text-orange-800 dark:text-orange-400"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <WifiOff className="w-4 h-4" />
                  <span className="text-sm font-ageo-regular">
                    {!networkStatus.navigatorOnline 
                      ? "Modo offline ativo. Upload de arquivos e sincronização indisponíveis."
                      : "Conexão instável. Servidor não acessível. Usando cache local."
                    }
                  </span>
                </div>
                {networkStatus.navigatorOnline && (
                  <button
                    onClick={forceCheckConnectivity}
                    className="text-sm hover:underline font-ageo-regular flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Verificar Conexão
                  </button>
                )}
              </div>
            </div>
          )}



          {/* ✅ INDICADOR DE PENDÊNCIAS DE SINCRONIZAÇÃO */}
          {pendingSync && (
            <div className="bg-orange-100 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-800 text-orange-800 dark:text-orange-400 p-3 rounded-lg mb-4 transition-colors">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                <span className="text-sm font-ageo-regular">
                  Há alterações não sincronizadas. Será enviado ao servidor quando estiver online.
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <Link to={`/obras/${obraId}/diarios`}>
              <button className={`px-3 py-1 rounded-md text-sm font-ageo-regular transition-colors flex items-center gap-2 ${
                isDarkMode 
                  ? "bg-gray-700 text-gray-100 hover:bg-gray-600" 
                  : "bg-secondary text-foreground hover:bg-secondary/80"
              }`}>
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>
            </Link>
            <h1 className={`text-lg font-ageo-bold truncate flex-1 text-center transition-colors ${
              isDarkMode ? "text-gray-100" : "text-foreground"
            }`}>
              Diário {relatorio?.numero || form.numero}
              {lastSavedAt && (
                <span
                  className="ml-2 text-xs px-2 py-0.5 rounded-full border
                             text-gray-600 border-gray-300
                             dark:text-gray-300 dark:border-gray-600"
                  title={`Rascunho salvo em ${new Date(lastSavedAt).toLocaleString()}`}
                >
                  Rascunho salvo às {new Date(lastSavedAt).toLocaleTimeString()}
                </span>
              )}
              {isFromCache && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full ml-2">
                  Cache
                </span>
              )}
            </h1>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-3 py-1 rounded-md text-sm font-ageo-regular focus:ring-2 disabled:opacity-60 transition-colors flex items-center gap-2 ${
                isDarkMode 
                  ? "bg-green-600 text-white hover:bg-green-700 focus:ring-green-400" 
                  : "bg-confirmation text-background hover:bg-confirmation/90 focus:ring-confirmation"
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Salvando…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isOnline ? 'Salvar' : 'Salvar Local'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Evita submits acidentais vindos de botões internos (ex.: ações nos comentários) */}
      {/* corpo com mesma régua e padding compacto */}
      <form onSubmit={(e) => e.preventDefault()} className="w-full max-w-[1600px] 2xl:max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 space-y-6 bg-transparent">
        {/* Erro */}
        {erro && (
          <div className={`border rounded-lg p-4 transition-colors ${
            erro.includes('sucesso') || erro.includes('salvo')
              ? (isDarkMode ? "bg-green-900/20 border-green-800/20" : "bg-confirmation/10 border-confirmation/20")
              : (isDarkMode ? "bg-red-900/20 border-red-800/20" : "bg-critical/10 border-critical/20")
          }`}>
            <p className={`font-ageo-regular transition-colors ${
              erro.includes('sucesso') || erro.includes('salvo')
                ? (isDarkMode ? "text-green-400" : "text-confirmation")
                : (isDarkMode ? "text-red-400" : "text-critical")
            }`}>{erro}</p>
          </div>
        )}

        <DiarioFormSections
          form={form}
          updateField={(field: string, value: any) => updateField(field as keyof FormData, value)}
          updateQtdMao={updateQtdMao}
          removeMao={removeMao}
          updateQtdEquip={updateQtdEquip}
          removeEquip={removeEquip}
          handleFileUpload={handleFileUpload}
          removeAnexo={removeAnexo}
          mediaItems={mediaItems}
          openGallery={openGallery}
          modoEscuro={isDarkMode}
          saving={saving}
          uploading={uploading && isOnline}
          setShowMaoModal={setShowMaoModal}
          setShowEquipModal={setShowEquipModal}
          onFotoLongPress={!modoSelecao ? handleFotoLongPress : undefined}
          modoSelecaoFotos={modoSelecao}
          fotosSelecionadas={fotosSelecionadas}
          onToggleSelecaoFoto={toggleFotoSelecionada}
          onToggleModoSelecao={toggleModoSelecao}
          onSelecionarTodasFotos={selecionarTodasFotos}
          onLimparSelecaoFotos={limparSelecaoFotos}
          onAbrirTagsMultiplas={abrirTagsMultiplasFotos}
          fotoTagsMap={fotoTagsMap}
        />

        {/* Botões de Ação para Mobile */}
        <div className="flex gap-4 pt-4 sm:hidden">
          <Link
            to={`/obras/${obraId}/diarios`}
            className={`flex-1 text-center px-4 py-3 rounded-md font-ageo-regular transition-colors ${
              isDarkMode 
                ? "bg-gray-700 text-gray-100 hover:bg-gray-600" 
                : "bg-secondary text-foreground hover:bg-secondary/80"
            }`}
          >
            Voltar
          </Link>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`flex-1 px-4 py-3 rounded-md font-ageo-regular disabled:opacity-60 transition-colors ${
              isDarkMode 
                ? "bg-green-600 text-white hover:bg-green-700" 
                : "bg-confirmation text-background hover:bg-confirmation/90"
            }`}
          >
            {saving ? "Salvando…" : (isOnline ? "Salvar Diário" : "Salvar Local")}
          </button>
        </div>
      </form>

      {/* Botões de Ação para Desktop */}
      <div className="w-full max-w-[1600px] 2xl:max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 hidden sm:flex justify-between gap-4 bg-transparent">
        <Link
          to={`/obras/${obraId}/diarios`}
          className={`px-3 py-1 rounded-md text-sm font-ageo-regular transition-colors flex items-center gap-2 ${
            isDarkMode 
              ? "bg-gray-700 text-gray-100 hover:bg-gray-600" 
              : "bg-secondary text-foreground hover:bg-secondary/80"
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Link>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-3 py-1 rounded-md text-sm font-ageo-regular focus:ring-2 disabled:opacity-60 transition-colors flex items-center gap-2 ${
            isDarkMode 
              ? "bg-green-600 text-white hover:bg-green-700 focus:ring-green-400" 
              : "bg-confirmation text-background hover:bg-confirmation/90 focus:ring-confirmation"
          }`}
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Salvando…
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isOnline ? 'Salvar' : 'Salvar Local'}
            </>
          )}
        </button>
      </div>

      {/* Visualizador de Fotos/Vídeos Unificado */}
      <PhotoViewer
        items={mediaItems.map(m => ({
          id: m.id,
          url: m.url,
          nome: m.nome,
          tipo: m.tipo as 'foto' | 'video' | 'anexo',
          criado_em: m.criado_em,
        }))}
        fotoAtualIndex={selectedMediaIndex ?? 0}
        isOpen={galleryOpen && selectedMediaIndex !== null}
        onClose={() => closeGallery(false)}
        onPrevious={goToPrevious}
        onNext={goToNext}
        modoEscuro={isDarkMode}
        tagsMap={fotoTagsMap}
        obraId={obraId}
        usuarioId={user?.id}
        onTagsChanged={recarregarTagsFotos}
      />

      <DiarioModals
        showMaoModal={showMaoModal}
        setShowMaoModal={setShowMaoModal}
        showEquipModal={showEquipModal}
        setShowEquipModal={setShowEquipModal}
        showAtvModal={showAtvModal}
        setShowAtvModal={setShowAtvModal}
        showOcorrModal={showOcorrModal}
        setShowOcorrModal={setShowOcorrModal}
        onAddMaoObra={handleAddMaoObra}
        onAddEquipamentos={handleAddEquipamentos}
        onAddAtividade={handleAddAtividade}
        onAddOcorrencia={handleAddOcorrencia}
        existingMaoObra={form.mao_obra}
        existingEquipamentos={form.equipamentos}
        modoEscuro={isDarkMode}
        saving={saving}
      />

      {/* Popover de Tags para Fotos */}
      {obraId && tagsPopoverOpen && (
        <FotoTagsPopover
          isOpen={tagsPopoverOpen}
          onClose={fecharTagsPopover}
          // Modo foto única (long press)
          fotoId={fotoParaTags?.id}
          fotoUrl={fotoParaTags?.url}
          // Modo múltiplas fotos (seleção)
          fotos={modoSelecao && fotosSelecionadas.size > 0
            ? Array.from(fotosSelecionadas).map(id => {
                const foto = form.anexos.find(f => f.id === id);
                return { id, url: foto?.url };
              })
            : undefined
          }
          obraId={obraId}
          usuarioId={user?.id}
          position={tagsPopoverPosition}
        />
      )}
    </div>
  );
}
